// Horizon toggle for the Bills page — lets users flip between rolling-day
// windows (2 weeks / 30 days / 90 days) and the classic "By month" view.
//
// Why this exists: the old month view answers "how did July look?" — great
// for reviewing. But the strongest day-to-day question is "what's hitting me
// between now and the next paycheck?", which is a rolling window, not a
// calendar month. This toggle lets both questions live in the same page.
import { formatMoney } from "@/lib/api";

export const HORIZONS = [
  // Long labels are shown ≥640px; the shorter `mobileLabel` keeps all four
  // options visible on 390px screens instead of the last one ("By month")
  // getting hidden inside an invisible horizontal scroll.
  { key: "14",    label: "Next 2 weeks",  mobileLabel: "2 wks",   days: 14 },
  { key: "30",    label: "Next 30 days",  mobileLabel: "30 days", days: 30 },
  { key: "90",    label: "Next 90 days",  mobileLabel: "90 days", days: 90 },
  { key: "month", label: "By month",      mobileLabel: "Month",   days: null },
];

// Segmented control. Compact + horizontally scrollable so it fits on the
// narrowest phones without stealing vertical space above the register.
export function HorizonToggle({ value, onChange }) {
  return (
    <div className="card-soft !p-2" data-testid="bills-horizon-toggle">
      <div
        className="inline-flex items-center gap-1 rounded-full border border-[#D8CFB9] bg-[#F9F8F6] p-0.5 text-xs w-full overflow-x-auto"
        role="group"
        aria-label="Bills horizon"
      >
        {HORIZONS.map((h) => {
          const active = value === h.key;
          return (
            <button
              key={h.key}
              type="button"
              onClick={() => onChange(h.key)}
              aria-pressed={active}
              data-testid={`bills-horizon-${h.key}`}
              className={`shrink-0 px-3 py-1.5 rounded-full font-semibold transition-colors whitespace-nowrap ${
                active
                  ? "bg-[#4A6148] text-white shadow-sm"
                  : "text-[#59605C] hover:text-[#2C302E]"
              }`}
            >
              <span className="sm:hidden">{h.mobileLabel || h.label}</span>
              <span className="hidden sm:inline">{h.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Small stat strip that summarises the currently-selected horizon window.
// Only rendered when the user is on a rolling-day view (not "By month"),
// because the month view already has month-level totals in the strip nav.
export function HorizonSummary({ days, bills, pastDueCount, pastDueTotal, sort, onSortChange }) {
  const total = bills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
  const unpaidBills = bills.filter((b) => b.status !== "paid" && b.status !== "cleared");
  const unpaidTotal = unpaidBills.reduce((s, b) => s + Number(b.estimated_due_amount || 0), 0);
  return (
    <div className="card-soft !p-4" data-testid="bills-horizon-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">
            Next {days} days
          </div>
          <div className="font-heading text-lg text-[#2C302E] mt-0.5">
            {bills.length} bill{bills.length === 1 ? "" : "s"} due
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <Stat label="Total owed" value={formatMoney(unpaidTotal)} tone="warn" testId="bills-horizon-summary-unpaid" />
          <Stat label="All bills" value={formatMoney(total)} testId="bills-horizon-summary-total" />
          {pastDueCount > 0 && (
            <Stat
              label={`Past due (${pastDueCount})`}
              value={`-${formatMoney(pastDueTotal)}`}
              tone="danger"
              testId="bills-horizon-summary-pastdue"
            />
          )}
        </div>
      </div>
      {onSortChange && (
        /* Sort segmented control. Shown only in rolling-window mode so users
           can flip between chronological (default) and biggest-first. Sits
           BELOW the stats so it doesn't fight for attention with the money
           numbers. */
        <div className="mt-3 pt-3 border-t border-[#EBE6E0] flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Sort by</span>
          <div
            className="inline-flex items-center gap-0.5 rounded-full border border-[#D8CFB9] bg-[#F9F8F6] p-0.5 text-xs"
            role="group"
            aria-label="Sort bills"
          >
            <button
              type="button"
              onClick={() => onSortChange("due")}
              aria-pressed={sort === "due"}
              data-testid="bills-horizon-sort-due"
              className={`px-3 py-1 rounded-full font-semibold transition-colors whitespace-nowrap ${
                sort === "due" ? "bg-[#4A6148] text-white shadow-sm" : "text-[#59605C] hover:text-[#2C302E]"
              }`}
            >
              Due date
            </button>
            <button
              type="button"
              onClick={() => onSortChange("amount")}
              aria-pressed={sort === "amount"}
              data-testid="bills-horizon-sort-amount"
              className={`px-3 py-1 rounded-full font-semibold transition-colors whitespace-nowrap ${
                sort === "amount" ? "bg-[#4A6148] text-white shadow-sm" : "text-[#59605C] hover:text-[#2C302E]"
              }`}
            >
              Amount
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testId }) {
  const color =
    tone === "danger" ? "text-[#8C3A30]"
    : tone === "warn" ? "text-[#7A4E1F]"
    : "text-[#2C302E]";
  return (
    <div className="text-right leading-tight" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">
        {label}
      </div>
      <div className={`font-heading text-base font-bold whitespace-nowrap ${color}`}>
        {value}
      </div>
    </div>
  );
}
